<script setup lang="ts">
import type { CrashCandle } from '~/types/crash-pre-game';

type Props = {
  /** A vela mais recente — índice 0 do `cycleHistory`. */
  candle: CrashCandle;
  /** "8s", "3min" desde o último payload. null = sem dado ou dado velho. */
  live?: string | null;
};

const { candle, live = null } = defineProps<Props>();

// Horário no fuso do JOGADOR: o core manda ISO em UTC, e "15:53:20" tem que ser
// 15:53:20 no relógio de quem está olhando.
const time = computed(() => {
  const parsed = Date.parse(candle.roundTime);

  if (Number.isNaN(parsed)) return '';

  return new Date(parsed).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
});

const value = computed(() => candle.multiplier.toFixed(2));
</script>

<template>
  <section class="crash-now">
    <!-- Mesmo ribbon do "Em média" das faixas: chip entreposto na borda de cima,
         com fundo sólido pra cortar a aresta do card em vez de ficar colado por
         cima dela. -->
    <p
      v-if="live"
      class="crash-now__live"
    >
      <span
        aria-hidden="true"
        class="crash-now__pulse"
      />
      Ao vivo, há <b>{{ live }}</b>
    </p>

    <div class="crash-now__box">
      <!-- A chave carrega o horário da rodada: vela nova = elemento novo = a
           animação de entrada roda de novo, sem watcher. -->
      <p
        :key="candle.roundTime"
        class="crash-now__value"
        :class="`crash-now__value--${candle.color}`"
      >
        {{ value }}<span>X</span>
      </p>

      <div class="crash-now__meta">
        <p class="crash-now__k">
          Última rodada, <span class="crash-now__time">{{ time }}</span>
        </p>
      </div>
    </div>

    <div class="crash-now__void">
      <span class="crash-now__label">agora</span>
    </div>
  </section>
</template>

<style scoped>
/* A margem de cima conta com o ribbon: ele sobe metade da altura PRA FORA do
   card, e sem essa folga encosta no que vier acima (no desktop, o botão de
   jogar). */
.crash-now {
  position: relative;
  margin-top: 20px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 44px;
  border-radius: 16px;
  background: #0f1727;
}

@media (min-width: 1024px) {
  .crash-now {
    margin-top: 26px;
    grid-template-columns: minmax(0, 1fr) 72px;
  }
}

.crash-now__box {
  display: flex;
  align-items: center;
  gap: 14px;
  /* Folga extra em cima: é onde a metade de dentro do ribbon cai. */
  padding: 18px 14px 13px;
}

@media (min-width: 1024px) {
  .crash-now__box {
    gap: 20px;
    padding: 22px 20px 16px;
  }
}

.crash-now__value {
  margin: 0;
  font-size: 44px;
  font-weight: 800;
  line-height: 0.9;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  animation: crash-now-in 420ms cubic-bezier(0.16, 0.84, 0.44, 1) both;
}

@media (min-width: 1024px) {
  .crash-now__value {
    font-size: 64px;
  }
}

.crash-now__value span {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0;
}

.crash-now__value--blue {
  color: #37c6f0;
}

.crash-now__value--purple {
  color: #8f5be6;
}

.crash-now__value--pink {
  color: #ff8fbe;
}

.crash-now__meta {
  min-width: 0;
}

.crash-now__k {
  margin: 0;
  color: #e9eef9;
  font-size: 13px;
  font-weight: 700;
}

.crash-now__time {
  font-variant-numeric: tabular-nums;
}

.crash-now__live {
  position: absolute;
  top: 0;
  left: 50%;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 3px 11px;
  transform: translate(-50%, -50%);
  border: 1px solid rgba(78, 216, 166, 0.35);
  border-radius: 999px;
  background: #070b14;
  color: rgba(255, 255, 255, 0.9);
  font-size: 11px;
  line-height: 1.4;
  white-space: nowrap;
}

.crash-now__live b {
  color: #e9eef9;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.crash-now__pulse {
  flex: none;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #4ed8a6;
  box-shadow: 0 0 10px rgba(78, 216, 166, 0.9);
}

/* A faixa vazia à direita é o "ainda não aconteceu": o vão depois da última
   vela, cortado pelo filete do agora.
   O canto arredondado é dela mesma: o `overflow: hidden` que fazia esse serviço
   no card saiu, senão comeria a metade de fora do ribbon. */
.crash-now__void {
  border-radius: 0 16px 16px 0;
  background: #070b14;
  border-left: 1px solid #37c6f0;
  box-shadow: -6px 0 22px rgba(55, 198, 240, 0.16);
}

.crash-now__label {
  display: inline-block;
  padding: 12px 0 0 6px;
  color: #37c6f0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  writing-mode: vertical-rl;
}

@media (min-width: 1024px) {
  .crash-now__label {
    padding: 14px 0 0 7px;
  }
}

@keyframes crash-now-in {
  from {
    opacity: 0;
    transform: translateX(16px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .crash-now__value {
    animation: none;
  }
}
</style>
